/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import type ApplicationInstance from "@ember/application/instance";
import { registerDestructor } from "@ember/destroyable";
import { action } from "@ember/object";
import { service } from "@ember/service";
import Component from "@glimmer/component";
import type { ChartConfiguration, ChartItem } from "chart.js";
import { Chart, RadarController, RadialLinearScale } from "chart.js";
import { renderText, toFont, toPadding } from "chart.js/helpers";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { task } from "ember-concurrency";
import type ChartJSService from "parog-web/services/chartjs";

interface Signature {
  Args: {
    points: number[];
    index: number;
  };
}

type DerivedRadarChartConfig = ChartConfiguration<"derivedRadar">;

function drawPointLabels(scale, labelCount) {
  const {
    ctx,
    options: { pointLabels }
  } = scale;

  for (let i = 0; i < labelCount; i++) {
    const optsAtIndex = pointLabels.setContext(scale.getPointLabelContext(i));
    const plFont = toFont(optsAtIndex.font);
    const { x, y } = scale._pointLabelItems[i];
    const nextPoint =
      scale._pointLabelItems[(i + 1) % scale._pointLabelItems.length];
    const xNextPoint = nextPoint["x"];
    const yNextPoint = nextPoint["y"];
    // Save the context state
    ctx.save();

    let newX = x + (xNextPoint - x) / 2;
    let newY = y + (yNextPoint - y) / 2;

    // Little adjustments so it's cleaner
    const adjustments = {
      1: { x: 5, y: 15 },
      2: { x: 10, y: 10 },
      3: { x: 0, y: 10 },
      4: { x: 0, y: 10 },
      5: { x: 0, y: 10 },
      6: { x: -5, y: 10 },
      7: { x: -10, y: 10 },
      8: { x: 0, y: 10 }
    };

    if (i in adjustments) {
      const { x, y } = adjustments[i];
      newX += x;
      newY += y;
    }
    // Calculate the position to draw the label
    ctx.translate(newX, newY); // Move the context to the point label's original position

    let angle = Math.atan2(yNextPoint - y, xNextPoint - x);

    if (i >= 3 && i <= 6) {
      angle += -Math.PI;
    }

    ctx.rotate(angle); // Rotate the context to align the label properly

    // Render text
    const padding = toPadding(optsAtIndex.backdropPadding);
    renderText(
      ctx,
      scale._pointLabels[i],
      -padding.left, // Center the text horizontally
      -padding.top, // Center the text vertically
      plFont,
      {
        color: optsAtIndex.color,
        textAlign: "center",
        textBaseline: "middle"
      }
    );

    // Restore the context state
    ctx.restore();
  }
}

class CustomRadialLinearScale extends RadialLinearScale {
  // For unknown reasons, in some cases, computeTickLimit returns NaN
  // in the default class, so we redefine the same to make sure it always
  // returns Number.POSITIVE_INFINITY;
  computeTickLimit() {
    return Number.POSITIVE_INFINITY;
  }
  drawGrid() {
    const opts = this.options;
    const labelCount = this["_pointLabels"].length;

    if (opts.pointLabels.display) {
      drawPointLabels(this, labelCount);
    }
  }
}

CustomRadialLinearScale.id = "derivedRadialLinearScale";
CustomRadialLinearScale.defaults = RadialLinearScale.defaults;

/**
 * Custom RadarContorller subclass to override updateElements
 * in order to position the values between grids instead of on the grid
 *
 */
class CustomRadarController extends RadarController {
  updateElements(points, start, count, mode) {
    // We need type assertion here because `getScaleForId` method returns a generic scale type
    const scale = this.getScaleForId("r") as CustomRadialLinearScale;
    const reset = mode === "reset";

    for (let i = start; i < start + count; i++) {
      const point = points[i];
      const options = this.resolveDataElementOptions(
        i,
        point.active ? "active" : mode
      );
      let pointPosition;
      if (!reset && options.borderColor != "rgba(255, 255, 255, 0.1)") {
        //#region This is the custom code not present in the super class RadarController
        pointPosition = scale?.getPointPosition(
          i,
          scale.getDistanceFromCenterForValue(this.getParsed(i).r),
          Math.PI / count
        );
        //#endregion
      } else {
        pointPosition = scale?.getPointPositionForValue(i, this.getParsed(i).r);
      }

      const x = reset ? scale?.xCenter : pointPosition.x;
      const y = reset ? scale?.yCenter : pointPosition.y;

      const properties = {
        x,
        y,
        angle: pointPosition.angle,
        skip: isNaN(x) || isNaN(y),
        options
      };

      this.updateElement(point, i, properties, mode);
    }
  }
}

CustomRadarController.id = "derivedRadar";
CustomRadarController.defaults = RadarController.defaults;

export default class extends Component<Signature> {
  declare chart?: Chart;
  declare element: HTMLCanvasElement;
  @service declare chartjs: ChartJSService;

  id = crypto.randomUUID();
  cache = new Map();
  declare width: number;
  declare height: number;

  areasColor = [
    ["rgba(249, 243, 229", "rgb(234.6, 207, 133.5)", "rgb(214.4, 174.5, 0)"],
    [
      "rgb(231.9, 238.2, 224.9)",
      "rgb(193.9, 220.8, 158.8)",
      "rgb(153.1, 205.5, 82.4)"
    ],
    [
      "rgb(233.9, 245.4, 233.1)",
      "rgb(156.1, 212.6, 153.7)",
      "rgb(44.6, 184.6, 77.9)"
    ],
    [
      "rgb(232.4, 246.5, 245.1)",
      "rgb(151.7, 217.8, 213.6)",
      "rgb(28.5, 135.0, 191.9)"
    ],
    [
      "rgb(233.1, 236.2, 245.9)",
      "rgb(139.4, 177.1, 215.5)",
      "rgb(28.5, 135, 191.9)"
    ],
    [
      "rgb(233.2, 232, 244)",
      "rgb(159.5, 162.1, 208.6)",
      "rgb(89, 104.5, 172.8)"
    ],
    [
      "rgb(236.9, 231.8, 244)",
      "rgb(178.7, 156.2, 202.3)",
      "rgb(131.9, 92.4, 163.3)"
    ],
    [
      "rgb(247.4, 233.5, 242.6)",
      "rgb(224.2, 163.5, 201)",
      "rgb(202.1, 85.9, 156.8)"
    ],
    [
      "rgb(254.4, 234.3, 233.6)",
      "rgb(240.6, 169, 165)",
      "rgb(226.8, 96, 103.8)"
    ],
    [
      "rgb(254.8, 238.4, 225.5)",
      "rgb(255, 172.4, 128.8)",
      "rgb(248.9, 111.9, 49.3)"
    ]
  ];

  @action
  createRadialGradient3(context, c1, c2, c3) {
    const chartArea = context.chart.chartArea;
    if (!chartArea) {
      // This case happens on initial chart load
      return;
    }

    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (this.width !== chartWidth || this.height !== chartHeight) {
      this.cache.clear();
    }
    let gradient = this.cache.get(c1 + c2 + c3);
    if (!gradient) {
      // Create the gradient because this is either the first render
      // or the size of the chart has changed
      this.width = chartWidth;
      this.height = chartHeight;
      const centerX = (chartArea.left + chartArea.right) / 2;
      const centerY = (chartArea.top + chartArea.bottom) / 2;
      const r = Math.min(
        (chartArea.right - chartArea.left) / 2,
        (chartArea.bottom - chartArea.top) / 2
      );

      const ctx = context.chart.ctx;
      gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        r
      );
      gradient.addColorStop(0, c1);
      gradient.addColorStop(0.5, c2);
      gradient.addColorStop(1, c3);

      this.cache.set(c1 + c2 + c3, gradient);
    }

    return gradient;
  }

  constructor(owner: ApplicationInstance, args: Signature["Args"]) {
    super(owner, args);
    registerDestructor(this, () => this.chart?.destroy());
    this.createOrUpdateChart.perform(args.points);
  }

  createOrUpdateChart = task({ restartable: true }, async points => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- Don't delete, this is needed for chartjs tests, otherwise Chart.instances is empty. For mysterious reasons.
    Chart;
    const borderColor = "black";
    const datasets: DerivedRadarChartConfig["data"]["datasets"] = [
      // This is the main dataset
      {
        data: points.map(i => i + 2),
        fill: false,
        min: 0,
        max: 12,
        borderColor,
        datalabels: {
          opacity: 1,
          borderColor: "#11469e",
          borderWidth: 2,
          borderRadius: 150,
          font: {
            weight: "bold",
            size: 10,
            lineHeight: 1 /* align v center */
          },
          /* hover styling */
          backgroundColor: context => (context.active ? borderColor : "white"),
          color: context => (context.active ? "white" : borderColor),
          listeners: {
            enter: function (context) {
              context.active = true;
              return true;
            },
            leave: function (context) {
              context.active = false;
              return true;
            }
          }
        }
      },
      // Those datasets are created to draw the gradients
      ...this.areasColor.map((areaColor, index) => {
        return {
          data: Array.from({ length: 10 }, (_v, i) =>
            i === index || i === (index + 1) % 10 ? 13 : 0
          ),
          min: 0,
          max: 12,
          fill: true,
          backgroundColor: context => {
            return this.createRadialGradient3(
              context,
              areaColor[0],
              areaColor[1],
              areaColor[2]
            );
          },
          borderColor: "rgba(255, 255, 255, 0.1)",
          pointBackgroundColor: "rgba(255, 255, 255, 0.1)",
          datalabels: {
            display: false
          }
        };
      })
    ];
    const data: DerivedRadarChartConfig["data"] = {
      labels: [
        ["Douleur", "abdominale"],
        ["Régulation de", "la défécation"],
        ["Vie", "sociale"],
        ["Activités", "professionnelles", "et sociales"],
        "Sommeil",
        "Energie",
        ["Niveau de", "stress,", "anxiété"],
        ["Image", "de soi"],
        ["Vie", "intime"],
        ["Douleur", "articulaire"]
      ],
      datasets: datasets
    };
    const options: DerivedRadarChartConfig["options"] = {
      maintainAspectRatio: false,
      // @ts-expect-error `responsible` is not a valid ChartOption, and I wonder if this is not a typo for `responsive` 🤔
      responsible: true,
      layout: {
        padding: 0 // Remove all padding around the chart
      },
      scales: {
        r: {
          type: "derivedRadialLinearScale",
          ticks: {
            display: false
          },
          suggestedMin: 0,
          suggestedMax: 13
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: false
        },
        datalabels: {
          /* formatter */
          formatter: function (value) {
            return value - 2;
          }
        }
      },
      elements: {
        line: {
          borderWidth: 3
        },
        arc: {
          backgroundColor: "black"
        }
      }
    };
    if (!this.chart) {
      const Chart = await this.chartjs.register();
      Chart.register(CustomRadarController, CustomRadialLinearScale);

      const canvas = document.getElementById(
        `ibd-disk-chart-${this.id}`
      ) as HTMLCanvasElement;

      if (canvas) {
        const ctx = canvas.getContext("2d") as ChartItem;
        this.chart = new Chart(ctx, {
          type: "derivedRadar",
          // registering ChartDataLabels locally instead of globally fixes a lot of random issues
          plugins: [ChartDataLabels],
          data,
          options
        });
      }
    } else {
      this.chart.data = data ?? this.chart.data;
      this.chart.options = options ?? this.chart.options;
      this.chart.update();
    }
  });
}

// Declare the new "derivedRadar" ChartType
declare module "chart.js" {
  interface ChartTypeRegistry {
    derivedRadar: {
      chartOptions: ChartTypeRegistry["radar"]["chartOptions"];
      datasetOptions: ChartTypeRegistry["radar"]["datasetOptions"];
      parsedDataType: ChartTypeRegistry["radar"]["parsedDataType"];
      scales: keyof CustomRadialLinearScale;
    };
  }
}
